import { useEffect, useState } from "react";
import styled, { keyframes } from "styled-components";
import { CloseOutlined } from "@ant-design/icons";
import { MainColor } from "../../style/styleValue";
import { useMessages } from "../../i18n";

interface NoticePopupProps {
  title?: string;
  storageKey?: string;
  children: React.ReactNode;
}

const DAY_MS = 24 * 60 * 60 * 1000;

const ko = {
  title: "안내",
  close: "닫기",
  hideToday: "오늘 하루 보지 않기",
};

const en: typeof ko = {
  title: "Notice",
  close: "Close",
  hideToday: "Don't show again today",
};

const NoticePopup = ({
  title,
  storageKey = "atlas-notice-hide-until",
  children,
}: NoticePopupProps) => {
  const [open, setOpen] = useState(false);
  const t = useMessages({ ko, en });

  useEffect(() => {
    try {
      const hideUntil = Number(localStorage.getItem(storageKey) || 0);
      if (!hideUntil || Date.now() > hideUntil) {
        setOpen(true);
      }
    } catch {
      setOpen(true);
    }
  }, [storageKey]);

  const close = () => setOpen(false);

  const hideForOneDay = () => {
    try {
      localStorage.setItem(storageKey, String(Date.now() + DAY_MS));
    } catch {
      /* localStorage 접근 불가 시 무시 */
    }
    setOpen(false);
  };

  if (!open) return null;

  return (
    <Backdrop onClick={close}>
      <Card onClick={(e) => e.stopPropagation()}>
        <Header>
          <span>{title ?? t.title}</span>
          <CloseButton onClick={close} aria-label={t.close}>
            <CloseOutlined />
          </CloseButton>
        </Header>

        <Body>
          <Logo src="/img/logo/ATLASLOGO1.png" alt="ATLAS" />
          <BodyText>{children}</BodyText>
        </Body>

        <FooterBar>
          <FooterLink onClick={hideForOneDay}>{t.hideToday}</FooterLink>
          <FooterDivider />
          <FooterLink onClick={close}>{t.close}</FooterLink>
        </FooterBar>
      </Card>
    </Backdrop>
  );
};

const fadeIn = keyframes`
  from { opacity: 0; }
  to   { opacity: 1; }
`;

const popIn = keyframes`
  from { opacity: 0; transform: translateY(16px) scale(0.98); }
  to   { opacity: 1; transform: translateY(0) scale(1); }
`;

const Backdrop = styled.div`
  position: fixed;
  inset: 0;
  z-index: 11050;
  background: rgba(0, 0, 0, 0.35);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 20px;
  animation: ${fadeIn} 0.2s ease;
`;

const Card = styled.div`
  width: 100%;
  max-width: 450px;
  background: #fff;
  border-radius: 14px;
  overflow: hidden;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.25);
  animation: ${popIn} 0.28s cubic-bezier(0.4, 0, 0.2, 1);
`;

const Header = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 16px 20px;
  background: ${MainColor};
  color: #fff;
  font-size: 16px;
  font-weight: 700;
  letter-spacing: 0.01em;
`;

const CloseButton = styled.button`
  background: transparent;
  border: none;
  color: rgba(255, 255, 255, 0.85);
  font-size: 16px;
  cursor: pointer;
  display: flex;
  align-items: center;
  padding: 4px;
  border-radius: 6px;
  transition: all 0.2s;

  &:hover {
    color: #fff;
    background: rgba(255, 255, 255, 0.15);
  }
`;

const Body = styled.div`
  padding: 28px 24px;
  color: #333;
  text-align: left;
`;

const Logo = styled.img`
  display: block;
  height: 70px;
  width: auto;
  object-fit: contain;
  margin: 0 auto 18px;
`;

const BodyText = styled.div`
  font-size: 15px;
  font-weight: 700;
  line-height: 1.75;
`;

const FooterBar = styled.div`
  display: flex;
  align-items: stretch;
  border-top: 1px solid #eee;
`;

const FooterLink = styled.button`
  flex: 1;
  background: #fafafa;
  border: none;
  padding: 14px 0;
  font-size: 14px;
  color: #666;
  cursor: pointer;
  transition: all 0.2s;

  &:hover {
    background: #f0f2f5;
    color: ${MainColor};
  }
`;

const FooterDivider = styled.div`
  width: 1px;
  background: #eee;
`;

export default NoticePopup;
